'use client'

import { useState, useEffect, useCallback } from 'react'
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'

export const BodyImageGallery = ({
  images,
  productName,
}: BodyImageGalleryProps) => {
  const validImages = images?.filter(img => img.id && img.src) || []
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const currentMainImage = validImages[currentImageIndex]?.src || ''

  const goToNext = useCallback(() => {
    setLightboxIndex(prev => (prev + 1) % validImages.length)
  }, [validImages.length])

  const goToPrevious = useCallback(() => {
    setLightboxIndex(
      prev => (prev - 1 + validImages.length) % validImages.length
    )
  }, [validImages.length])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isLightboxOpen) return

      if (e.key === 'Escape') {
        setIsLightboxOpen(false)
      } else if (e.key === 'ArrowLeft') {
        goToPrevious()
      } else if (e.key === 'ArrowRight') {
        goToNext()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isLightboxOpen, lightboxIndex, goToNext, goToPrevious])

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isLightboxOpen])

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setIsLightboxOpen(true)
  }

  const handleImageHover = (index: number) => {
    setCurrentImageIndex(index)
  }

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  if (!validImages.length) {
    return null
  }

  return (
    <>
      {/* Main Gallery */}
      <div className="ml-0 md:ml-8 w-full md:w-96 bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl shadow-lg md:float-right border border-gray-200 mb-6 md:mb-0">
        {/* Main Image Container */}
        <div
          className="relative w-full bg-white rounded-lg overflow-hidden shadow-md group cursor-zoom-in hover:shadow-lg transition-all duration-300"
          style={{ aspectRatio: '16/9' }}
          onClick={() => openLightbox(currentImageIndex)}
        >
          {currentMainImage ? (
            <img
              src={currentMainImage}
              className="w-full h-full object-contain transition-all duration-300 group-hover:scale-105"
              alt={
                productName && validImages.length > 1
                  ? `${productName} - image ${currentImageIndex + 1}`
                  : productName || `Product image ${currentImageIndex + 1}`
              }
              onError={e => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500">
              No image available
            </div>
          )}

          {/* Zoom overlay */}
          <div className="absolute inset-0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
            <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg font-semibold flex items-center">
              <MagnifyingGlassIcon className="w-5 h-5 mr-2 text-white" />
              Click to zoom
            </div>
          </div>
        </div>

        {/* Thumbnail Grid */}
        {validImages.length > 1 && (
          <div className="mt-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-2">
              {validImages.map((image, index) => (
                <div
                  key={image.id}
                  className={`relative bg-white rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md group ${
                    index === currentImageIndex
                      ? 'shadow-md'
                      : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
                  }`}
                  style={{ aspectRatio: '4/3' }}
                  onClick={() => openLightbox(index)}
                  onMouseEnter={() => handleImageHover(index)}
                >
                  <img
                    src={image.src || ''}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    alt={
                      productName && validImages.length > 1
                        ? `${productName} - thumbnail ${index + 1}`
                        : productName || `Gallery image ${index + 1}`
                    }
                    onError={e => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  {index === currentImageIndex && (
                    <div className="absolute inset-0 bg-black/10"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <Modal
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        maxWidth="6xl"
        showCloseButton={false}
        fullScreen={false}
        className="relative z-[9999] mx-4 mb-4 mt-24 sm:m-4"
      >
        <div className="relative flex min-h-[420px] w-full items-center justify-center px-4 pb-28 pt-20 sm:h-full sm:min-h-0 sm:p-8">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-3 bg-white/80 hover:bg-white/100 rounded-full transition-all duration-200 text-black hover:scale-110 cursor-pointer shadow-lg"
            aria-label="Close lightbox"
          >
            <XMarkIcon className="w-6 h-6 text-black" />
          </button>

          {/* Navigation Arrows */}
          {validImages.length > 1 && (
            <>
              <button
                onClick={e => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 md:p-4 bg-white/80 hover:bg-white/100 rounded-full transition-all duration-200 text-black hover:scale-110 cursor-pointer shadow-lg"
                aria-label="Previous image"
              >
                <ChevronLeftIcon className="w-6 h-6 text-black" />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 md:p-4 bg-white/80 hover:bg-white/100 rounded-full transition-all duration-200 text-black hover:scale-110 cursor-pointer shadow-lg"
                aria-label="Next image"
              >
                <ChevronRightIcon className="w-6 h-6 text-black" />
              </button>
            </>
          )}

          {/* Main Lightbox Image */}
          <div
            className="relative w-full flex items-center justify-center"
            style={{ aspectRatio: '16/9' }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={validImages[lightboxIndex]?.src || ''}
              className="w-full h-full object-contain rounded-lg shadow-2xl"
              alt={
                productName && validImages.length > 1
                  ? `${productName} - image ${lightboxIndex + 1}`
                  : productName || `Full size image ${lightboxIndex + 1}`
              }
              onError={e => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>

          {/* Image Info */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/80 backdrop-blur-sm text-black px-4 py-2 rounded-full text-sm shadow-lg">
            {lightboxIndex + 1} of {validImages.length}
          </div>

          {/* Thumbnail Strip */}
          {validImages.length > 1 && (
            <div className="absolute bottom-16 md:bottom-20 left-1/2 transform -translate-x-1/2 flex gap-1 md:gap-2 bg-white/80 backdrop-blur-sm p-2 md:p-3 rounded-lg max-w-[85vw] md:max-w-[80vw] overflow-x-auto shadow-lg">
              {validImages.map((image, index) => (
                <div
                  key={image.id}
                  className={`flex-shrink-0 w-16 h-9 md:w-20 md:h-11 rounded-md overflow-hidden cursor-pointer transition-all duration-200 ${
                    index === lightboxIndex
                      ? 'ring-2 ring-black ring-offset-2 ring-offset-transparent'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ aspectRatio: '16/9' }}
                  onClick={e => {
                    e.stopPropagation()
                    setLightboxIndex(index)
                  }}
                >
                  <img
                    src={image.src || ''}
                    className="w-full h-full object-contain"
                    alt={
                      productName && validImages.length > 1
                        ? `${productName} - thumbnail ${index + 1}`
                        : productName || `Lightbox thumbnail ${index + 1}`
                    }
                    onError={e => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

interface BodyImageGalleryProps {
  images:
    | {
        id?: number | null
        src?: string | null
      }[]
    | null
    | undefined
  productName?: string | null
}
